import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import {
  CONSTANT,
  HttpRequestOptions,
  Session,
  SKIP_OPTION,
  Token,
} from '@seech/auth-ng';
import {
  AccountService,
  type AccountMemoryHistoryQuery,
  type AccountMemoryTimelineQuery,
  type ArchiveUserContactRequest,
  type ArchiveUserMemoryRequest,
  type AuthSessionResponse,
  type DeleteUserMemoryRequest,
  type GetUserContactRequest,
  type LearnUserMemoryRequest,
  type LoginRequest,
  type QueryUserContactsRequest,
  type QueryUserMemoryRequest,
  type QueryUserMemoryReviewRequest,
  type RefreshSessionRequest,
  type ResolveUserMemoryReviewRequest,
  type SignupRequest,
  type SwitchWorkspaceRequest,
  type UpdateUserContactRequest,
  type UpdateUserDynamicContextRequest,
  type UpdateUserMemoryRequest,
  type UpdateUserSettingsRequest,
  type UpsertUserContactRequest,
  type UpsertUserMemoryRequest,
  type UpsertUserOnboardingProfileRequest,
  type UserContextResponse,
  type UserContactQueryResponse,
  type UserContactResponse,
  type UserMemoryEntryResponse,
  type UserMemoryHistoryPageResponse,
  type UserMemoryQueryResponse,
  type UserMemoryReviewItemResponse,
  type UserMemoryReviewQueueResponse,
  type UserMemorySnapshotResponse,
  type UserMemoryTimelineEventResponse,
  type UserOnboardingProfileResponse,
  type UserSettingsResponse,
} from '@automatick/shared-ng';
import { Observable, map } from 'rxjs';

export type AccountLoginUser = UserContextResponse & {
  sessions: Session[];
};

@Injectable({
  providedIn: 'root',
})
export class AccountClientService {
  private readonly api = inject(AccountService);
  private readonly http = inject(HttpClient);

  signup(args: {
    body?: SignupRequest | null;
  }): Observable<AuthSessionResponse> {
    return this.api.apiAccountSignupPost(args.body ?? undefined);
  }

  login(
    option: HttpRequestOptions = {},
  ): Observable<HttpResponse<AccountLoginUser | null>> {
    const context = (option.context ?? new HttpContext())
      .set(SKIP_OPTION.AUTHENTICATION, true)
      .set(SKIP_OPTION.HEADERS, true);
    const globalToken = this.readHeaderValue(
      option.headers,
      CONSTANT.GLOBAL_TOKEN_KEY,
    );
    const headers = this.toHttpHeaders(option.headers);

    // The generated login method does not expose per-request headers, but login
    // must preserve the auth package's X-Seech-Token request option.
    return this.http
      .get<AuthSessionResponse>('/api/Account/login', {
        headers,
        observe: 'response',
        context,
      })
      .pipe(
        map((response) =>
          response.clone({
            body: this.toAccountLoginUser(response.body, globalToken),
          }),
        ),
      );
  }

  loginPost(args: {
    body?: LoginRequest | null;
  }): Observable<AuthSessionResponse> {
    return this.api.apiAccountLoginPost(args.body ?? undefined);
  }

  refresh(args: {
    body: RefreshSessionRequest;
  }): Observable<AuthSessionResponse> {
    return this.api.apiAccountRefreshPost(args.body);
  }

  switchWorkspace(args: {
    body: SwitchWorkspaceRequest;
  }): Observable<AuthSessionResponse> {
    return this.api.apiAccountSwitchWorkspacePost(args.body);
  }

  logout(): Observable<boolean> {
    return this.api.apiAccountLogoutPost();
  }

  me(): Observable<UserContextResponse> {
    const context = new HttpContext().set(SKIP_OPTION.RESPONSE, true);
    return this.api.apiAccountMeGet('body', false, { context });
  }

  getSettings(): Observable<UserSettingsResponse> {
    return this.api.apiAccountSettingsGet();
  }

  updateSettings(args: {
    body: UpdateUserSettingsRequest;
  }): Observable<UserSettingsResponse> {
    return this.api.apiAccountSettingsPatch(args.body);
  }

  getOnboardingProfile(): Observable<UserOnboardingProfileResponse> {
    return this.api.apiAccountOnboardingProfileGet();
  }

  upsertOnboardingProfile(args: {
    body: UpsertUserOnboardingProfileRequest;
  }): Observable<UserOnboardingProfileResponse> {
    return this.api.apiAccountOnboardingProfilePut(args.body);
  }

  updateDynamicContext(args: {
    body: UpdateUserDynamicContextRequest;
  }): Observable<UserOnboardingProfileResponse> {
    return this.api.apiAccountOnboardingDeviceContextPost(args.body);
  }

  getMemorySnapshot(): Observable<UserMemorySnapshotResponse> {
    return this.api.apiAccountMemoryGet();
  }

  queryMemory(args: {
    body: QueryUserMemoryRequest;
  }): Observable<UserMemoryQueryResponse> {
    return this.api.apiAccountMemoryQueryPost(args.body);
  }

  getMemoryTimeline(args: {
    query?: AccountMemoryTimelineQuery;
  }): Observable<UserMemoryTimelineEventResponse[]> {
    return this.api.apiAccountMemoryTimelineGet(
      args.query?.take,
      args.query?.beforeEventLogId ?? undefined,
    );
  }

  getMemoryHistory(args: {
    memoryId: string;
    query?: AccountMemoryHistoryQuery;
  }): Observable<UserMemoryHistoryPageResponse> {
    return this.api.apiAccountMemoryMemoryIdHistoryGet(
      args.memoryId,
      args.query?.take,
      args.query?.beforeCreatedOn ?? undefined,
    );
  }

  learnMemory(args: {
    body: LearnUserMemoryRequest;
  }): Observable<UserMemoryEntryResponse[]> {
    return this.api.apiAccountMemoryLearnPost(args.body);
  }

  upsertMemory(args: {
    body: UpsertUserMemoryRequest;
  }): Observable<UserMemoryEntryResponse> {
    return this.api.apiAccountMemoryPost(args.body);
  }

  updateMemory(args: {
    memoryId: string;
    body: UpdateUserMemoryRequest;
  }): Observable<UserMemoryEntryResponse> {
    return this.api.apiAccountMemoryMemoryIdPatch(args.memoryId, args.body);
  }

  archiveMemory(args: {
    memoryId: string;
    body?: ArchiveUserMemoryRequest | null;
  }): Observable<UserMemoryEntryResponse> {
    return this.api.apiAccountMemoryMemoryIdArchivePatch(
      args.memoryId,
      args.body ?? undefined,
    );
  }

  queryMemoryReviews(args: {
    body: QueryUserMemoryReviewRequest;
  }): Observable<UserMemoryReviewQueueResponse> {
    return this.api.apiAccountMemoryReviewsQueryPost(args.body);
  }

  resolveMemoryReview(args: {
    reviewId: string;
    body: ResolveUserMemoryReviewRequest;
  }): Observable<UserMemoryReviewItemResponse> {
    return this.api.apiAccountMemoryReviewsReviewIdPatch(
      args.reviewId,
      args.body,
    );
  }

  deleteMemory(args: {
    memoryId: string;
    body?: DeleteUserMemoryRequest | null;
  }): Observable<boolean> {
    return this.api.apiAccountMemoryMemoryIdDelete(
      args.memoryId,
      args.body ?? undefined,
    );
  }

  queryContacts(args: {
    body: QueryUserContactsRequest;
  }): Observable<UserContactQueryResponse> {
    return this.api.apiAccountContactsQueryPost(args.body);
  }

  getContact(args: { contactId: string }): Observable<UserContactResponse> {
    return this.api.apiAccountContactsContactIdGet(args.contactId);
  }

  lookupContact(args: {
    body: GetUserContactRequest;
  }): Observable<UserContactResponse> {
    return this.api.apiAccountContactsLookupPost(args.body);
  }

  upsertContact(args: {
    body: UpsertUserContactRequest;
  }): Observable<UserContactResponse> {
    return this.api.apiAccountContactsPost(args.body);
  }

  updateContact(args: {
    contactId: string;
    body: UpdateUserContactRequest;
  }): Observable<UserContactResponse> {
    return this.api.apiAccountContactsContactIdPatch(args.contactId, args.body);
  }

  archiveContact(args: {
    contactId: string;
    body?: ArchiveUserContactRequest | null;
  }): Observable<UserContactResponse> {
    return this.api.apiAccountContactsContactIdArchivePatch(
      args.contactId,
      args.body ?? undefined,
    );
  }

  deleteContact(args: { contactId: string }): Observable<boolean> {
    return this.api.apiAccountContactsContactIdDelete(args.contactId);
  }

  private toAccountLoginUser(
    payload: AuthSessionResponse | null,
    globalTokenHeader: string | null,
  ): AccountLoginUser | null {
    const session = payload;
    if (!session?.user) {
      return null;
    }

    const globalToken =
      this.stripBearerPrefix(globalTokenHeader) ?? session.accessToken ?? '';
    const expiresOn =
      session.accessTokenExpiresOn ??
      session.refreshTokenExpiresOn ??
      new Date(Date.now() + 15 * 60 * 1000).toISOString();

    const token: Token = {
      access_token: session.accessToken ?? '',
      token_type: 'Bearer',
      expires: expiresOn,
      ...(session.refreshToken ? { refresh_token: session.refreshToken } : {}),
    };

    const accountSession: Session = {
      id: session.user.sessionId ?? '',
      token,
      globalToken: {
        access_token: globalToken,
        token_type: 'Bearer',
        expires: expiresOn,
      },
    };

    return {
      ...session.user,
      isAuthenticated: session.user.isAuthenticated ?? true,
      sessions: [accountSession],
    };
  }

  private readHeaderValue(
    headers: HttpRequestOptions['headers'],
    key: string,
  ): string | null {
    if (!headers) {
      return null;
    }

    if (headers instanceof HttpHeaders) {
      return headers.get(key);
    }

    const normalizedKey = key.toLowerCase();
    const headerKey = Object.keys(headers).find(
      (candidate) => candidate.toLowerCase() === normalizedKey,
    );
    const value = headerKey ? headers[headerKey] : null;
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }

    return typeof value === 'string' ? value : null;
  }

  private toHttpHeaders(
    headers: HttpRequestOptions['headers'],
  ): HttpHeaders | undefined {
    if (!headers) {
      return undefined;
    }

    return headers instanceof HttpHeaders ? headers : new HttpHeaders(headers);
  }

  private stripBearerPrefix(value: string | null): string | null {
    if (!value) {
      return null;
    }

    const trimmed = value.trim();
    return trimmed.slice(0, 'Bearer '.length).toLowerCase() === 'bearer '
      ? trimmed.slice('Bearer '.length).trim()
      : trimmed;
  }
}
